/*
 * File: AppointmentsPage.js
 * Project: mp-mobile-app
 *
 * Created by Vamsi Sureddy
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Handles the page that shows a list of appointments
 */
import React from "react";
import { observer } from "mobx-react";
import AssignmentTurnedIn from "@material-ui/icons/AssignmentTurnedIn";
import ApptCalendarDialog from "../components/ApptCalendarDialog";
import ApptPageToolbar from "../components/ApptPageToolbar";
import Appointments from "../components/Appointments";
import ApptFormDialog from "../components/ApptFormDialog";
import AppointmentPatientSelectionDialog from "../components/AppointmentPatientSelectionDialog";
import ApptFiltersDialog from "../components/ApptFiltersDialog";
import ApptDayBottomNavigation from "../components/ApptDayBottomNavigation";
import UIStore from "../store/UIStore";
import ApptStore from "services/appointments/store/DataStore";
import { createStyles, Theme, withStyles } from "@material-ui/core";
import UIFab from "components/ui/UIFab";
import { StoreContext } from "utils/useStore";
import { WithStyles } from "@material-ui/styles";
import { Instance } from "mobx-state-tree";
import AppStore from "Store";
import ApptModel from "../store/ApptModel";

const styles = (theme: Theme) =>
  createStyles({
    fabClass: {
      bottom: theme.spacing(2) + 30
    },
    apptContent: {
      display: "flex",
      flexDirection: "column",
      flex: "1 1 auto",
      overflow: "auto"
    }
  });

interface Props extends WithStyles<typeof styles> {
  appStore: Instance<typeof AppStore>;
}

class AppointmentsPage extends React.Component<Props> {
  static contextType = StoreContext;
  context!: React.ContextType<typeof StoreContext>;

  async componentDidMount() {
    this.props.appStore.setTitle("Schedule");
    await this.apptStore.fetchAppts(this.context);
  }

  get apptStore() {
    if (this.context === undefined) {
      throw new Error("Appointments Page is missing StoreContext");
    }
    return this.context.getStore(ApptStore);
  }

  get uiStore() {
    if (this.context === undefined) {
      throw new Error("Appointments Page is missing StoreContext");
    }

    return this.context.getStore(UIStore);
  }

  closeDateSelect = () => {
    if (this.context === undefined) {
      throw new Error("Appointments Page is missing StoreContext");
    }
    this.uiStore.toggleCalendarDialog();
    this.apptStore.reloadAppts(this.context);
  };

  selectDate = (date: Date | null) => {
    if (!date) {
      //"Go To Today"/clear selected date
      this.uiStore.setDate(new Date());
    } else {
      this.uiStore.setDate(date);
    }
    this.closeDateSelect();
  };

  handleNewAppt = (newAppt: Instance<typeof ApptModel>) => {
    const date = this.uiStore.date || new Date();
    newAppt.update("appointmentDate", date);
    newAppt.update("appointmentTime", date);
    newAppt.update("startTime", date);
    newAppt.update("length", 30);
    this.uiStore.togglePatientSelection();
  };

  render() {
    const classes = this.props.classes;
    const { newAppt } = this.apptStore;
    const { showCalendarDialog } = this.uiStore;

    return (
      <React.Fragment>
        <AppointmentPatientSelectionDialog />

        <ApptFormDialog
          title="New Appointment"
          appt={newAppt}
          store={this.apptStore}
        />

        <ApptCalendarDialog
          open={showCalendarDialog}
          onClose={this.closeDateSelect}
          onSelect={this.selectDate}
        />

        <ApptFiltersDialog />
        <ApptPageToolbar />
        <div className={classes.apptContent}>
          <Appointments />
        </div>
        <ApptDayBottomNavigation />
        <UIFab
          icon={<AssignmentTurnedIn />}
          handleClick={() => this.handleNewAppt(newAppt)}
          fabClassName={classes.fabClass}
          name={"New Appointment"}
        />
      </React.Fragment>
    );
  }
}

export default withStyles(styles)(observer(AppointmentsPage));
